// pages/FeedPage.jsx

import { useEffect, useRef, useState, useCallback } from "react";
import Actions from "../components/Post/Actions";
import FeedContainer from "../components/Feed/FeedContainer";
import { fetchPosts } from "../components/Feed/PostsData";
import { trackEvent } from "../utils/analytics";

const CATEGORIES = [
  { value: "all",      label: "All" },
  { value: "fashion",  label: "Fashion" },
  { value: "beauty",   label: "Beauty" },
  { value: "watches",  label: "Watches" },
  { value: "perfume",  label: "Perfume" },
  { value: "home",     label: "Home" },
  { value: "food",     label: "Food" },
  { value: "cars",     label: "Cars" },
];

const FeedPage = () => {
  const [posts, setPosts] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [algorithm, setAlgorithm] = useState("v1");

  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const dwellTimers = useRef({});

  // Reload from page 1 whenever category or algorithm changes
  useEffect(() => {
    Object.values(dwellTimers.current).forEach(clearTimeout);
    dwellTimers.current = {};
    pageRef.current = 1;
    loadingRef.current = false;
    setActiveIndex(0);
    setHasMore(true);

    const load = async () => {
      setInitialLoading(true);
      const { posts: data, next } = await fetchPosts(1, selectedCategory, algorithm);
      setPosts(data);
      setHasMore(!!next);
      setInitialLoading(false);
    };
    load();
  }, [selectedCategory, algorithm]);

  // Load next page (infinite scroll)
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoadingMore(true);

    const nextPage = pageRef.current + 1;
    const { posts: more, next } = await fetchPosts(nextPage, selectedCategory, algorithm);

    setPosts((prev) => [...prev, ...more]);
    pageRef.current = nextPage;
    setHasMore(!!next);

    loadingRef.current = false;
    setLoadingMore(false);
  }, [hasMore, selectedCategory, algorithm]);

  // Intersection observer — track active post + trigger load more
  useEffect(() => {
    if (!posts.length) return;
    itemRefs.current = itemRefs.current.slice(0, posts.length);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.dataset.index);
          const postId = posts[idx]?.id;
          if (entry.isIntersecting) {
            setActiveIndex(idx);
            if (idx >= posts.length - 5) loadMore();
            // Immediate impression for algorithm performance tracking
            if (postId) {
              trackEvent('feed_algorithm_view', { algorithm, position: idx, postId });
            }
            // 2-second dwell → post_view
            if (postId && !dwellTimers.current[postId]) {
              dwellTimers.current[postId] = setTimeout(() => {
                trackEvent('post_view', { postId });
                delete dwellTimers.current[postId];
              }, 2000);
            }
          } else if (postId && dwellTimers.current[postId]) {
            clearTimeout(dwellTimers.current[postId]);
            delete dwellTimers.current[postId];
          }
        });
      },
      { root: containerRef.current, threshold: 0.6 }
    );

    itemRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [posts, loadMore, algorithm]);

  if (initialLoading) {
    return (
      <div className="w-full h-screen bg-black flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full h-screen">
      <div className="flex lg:flex-row flex-nowrap lg:overflow-hidden overflow-x-auto snap-x snap-mandatory scroll-smooth">

        {/* LEFT — Posts + category chips */}
        <div className="lg:w-1/2 w-full h-screen snap-start shrink-0 flex flex-col">

          {/* Category chip bar — real layout space so image doesn't go behind it */}
          <div className="shrink-0 bg-black pt-2 pb-3 px-3">
            <div className="flex gap-2 overflow-x-auto"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
              {/* For You / Recent toggle */}
              <div className="shrink-0 flex rounded-full border border-white/10 overflow-hidden text-[10px] font-medium">
                <button
                  onClick={() => setAlgorithm("v1")}
                  className={`px-2.5 py-1 transition-all ${
                    algorithm === "v1" ? "bg-white text-black" : "text-white/50 hover:text-white/70"
                  }`}
                >
                  For You
                </button>
                <button
                  onClick={() => setAlgorithm("chronological")}
                  className={`px-2.5 py-1 transition-all ${
                    algorithm === "chronological" ? "bg-white text-black" : "text-white/50 hover:text-white/70"
                  }`}
                >
                  Recent
                </button>
              </div>
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium
                    transition-all duration-200
                    ${selectedCategory === cat.value
                      ? "bg-white text-black"
                      : "bg-white/10 text-white/60 border border-white/10 hover:bg-white/20"
                    }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Snap scroll — flex-1 so it fills space below the chip bar */}
          <div
            ref={containerRef}
            className="flex-1 min-h-0 overflow-y-scroll snap-y snap-mandatory bg-black scroll-smooth"
          >
            {posts.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center gap-3 px-8">
                {selectedCategory === "all" ? (
                  <>
                    <p className="text-white/30 text-lg">Nothing here yet</p>
                    <p className="text-white/20 text-sm">Follow people to see their posts</p>
                  </>
                ) : (
                  <>
                    <p className="text-white/30 text-lg text-center">
                      No posts in this category yet.
                    </p>
                    <p className="text-white/20 text-sm">Be the first!</p>
                  </>
                )}
              </div>
            ) : (
              posts.map((post, index) => (
                <div
                  key={post.id || index}
                  data-index={index}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className="snap-start h-full flex items-center justify-center"
                >
                  <Actions
                    id={post.id}
                    image={post.image}
                    images={post.images}
                    username={post.username}
                    caption={post.caption}
                    is_liked={post.is_liked}
                    likes_count={post.like_count}
                    comment_count={post.comment_count}
                    is_bookmarked={post.is_saved}
                    postAuthorId={post.author?.id}
                    links={post.links || []}
                    onDelete={(deletedId) => {
                      setPosts(prev => prev.filter(p => p.id !== deletedId));
                      setActiveIndex(i => Math.max(0, i));
                    }}
                  />
                </div>
              ))
            )}

            {loadingMore && (
              <div className="h-20 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white/20 border-t-white
                  rounded-full animate-spin" />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Details */}
        <div className="lg:w-1/2 w-full h-screen snap-start shrink-0">
          <div className="h-full overflow-hidden bg-black px-8 pt-8 pb-[calc(96px+env(safe-area-inset-bottom))]">
            {posts.length > 0 && (() => {
              const p = posts[activeIndex];
              return (
                <FeedContainer
                  username={p.username}
                  quote={p.caption}
                  profile_icon={p.profile_icon}
                  postAuthorId={p.author?.id}
                  profile_id={p.profile_id}
                  is_following={p.is_following}
                  chat=""
                />
              );
            })()}
          </div>
        </div>

      </div>
    </div>
  );
};

export default FeedPage;
